<template>
    <div
        class="well-card-wrapper"
        :class="{
            'timer-compensate': timerVisible
        }"
    >
        <div
            v-if="timerPlaceholder"
            class="timer-placeholder"
        >
            <div class="fw-700 mx-h">{{ timerText }}</div>
            <div class="fw-800 timer">00:00:00:00</div>
        </div>
        <Card
            class="well-card px-2 py-3 p-sm-5"
            :class="{
                'dark': dark,
            }"
        >
            <div
                v-if="timerElement"
                class="timer-element mx-n2 mt-n3 mb-3 mx-sm-n5 mt-sm-n5 mb-sm-5"
            >
                <div class="fw-700 mx-h">{{ timerText }}</div>
                <div class="d-flex fw-800 timer mx-h">
                    <div>
                        {{ left.days }}:
                    </div>

                    <div>
                        {{ left.hours }}:
                    </div>

                    <div>
                        {{ left.minutes }}:
                    </div>

                    <div>
                        {{ left.seconds }}
                    </div>
                </div>
            </div>
            <div
                v-if="checkmark"
                class="checkmark mt-n3 mt-sm-n5 mx-n2 mx-sm-n5 mb-2 p-2"
            >
                <img
                    class="d-block mx-auto"
                    src="/images/creative-practice/icon-subscribe-checkmark.svg"
                    alt=""
                    loading="lazy"
                    width="67"
                    height="67"
                />
            </div>

            <slot />
        </Card>
    </div>
</template>


<script>
    import Card from '~/components/cards/Card.vue';

    export default {
        props: {
            checkmark: Boolean,
            dark:      Boolean,
            timer:     [Boolean, String, Date],
            timerText: String,
        },

        components: {
            Card,
        },

        data () {
            return {
                timerInterval: null,
                timerEnd: null,
                left: {
                    days: '00',
                    hours: '00',
                    minutes: '00',
                    seconds: '00'
                },
            };
        },

        computed: {
            timerVisible () {
                return this.timerPlaceholder
                    || this.timerElement;
            },

            timerPlaceholder () {
                return this.timer === true;
            },

            timerElement () {
                return this.timerEnd instanceof Date
                    && this.timerEnd >= new Date();
            },
        },

        created () {
            this.updateTimerState();
        },

        mounted () {
            if (process.browser) this.startTimer();
        },

        methods: {
            checkTimer () {
                if (!this.timer) return false;

                if (typeof this.timer == 'string') {
                    if (isNaN(Date.parse(this.timer))) return false;

                    this.timerEnd = new Date(this.timer);

                    return true;
                }

                if (!(this.timer instanceof Date)) return false;

                this.timerEnd = this.timer;

                return true;
            },

            startTimer () {
                if (!this.checkTimer()) return;

                this.timerInterval = setInterval(() => {
                    this.updateTimerState();
                }, 1000)
            },

            updateTimerState () {
                if (!this.checkTimer()) return;

                const ended = this.timerEnd < new Date();

                if (ended) {
                    clearInterval(this.timerInterval);
                } else {
                    this.updateTime();
                }
            },

            updateTime () {
                if (!this.checkTimer()) return;

                // Update timer
                let now      = new Date();
                let interval = this.timerEnd.getTime() - now.getTime();
                let seconds  = Math.floor(interval / 1000);
                let minutes  = Math.floor(seconds / 60);
                let hours    = Math.floor(minutes / 60);
                let days     = Math.floor(hours / 24);

                hours   = hours   % 24;
                minutes = minutes % 60;
                seconds = seconds % 60;

                this.left = {
                    days:    ("00" + days).slice(-2),
                    hours:   ("00" + hours).slice(-2),
                    minutes: ("00" + minutes).slice(-2),
                    seconds: ("00" + seconds).slice(-2)
                };
            },
        },
    }
</script>


<style lang="postcss" scoped>
    .well-card-wrapper {
        margin: 0 auto;

        display:     flex;
        flex-flow:   column nowrap;
        align-items: stretch;

        @media screen and (min-width: 576px) {
            margin: 12px auto;

            max-width: 456px;
        }

        @media screen and (min-width: 992px) {
            margin: 20px;
        }

        &.timer-compensate {
            @media screen and (min-width: 992px) {
                margin-top: 8px;
            }
        }
    }

    .well-card {
        text-align: center;

        border-radius: 0;

        flex: 1 1 auto;

        @media screen and (max-width: 575px) {
            box-shadow: none;

            flex: 1 1 auto;
        }

        @media screen and (min-width: 576px) {
            border-radius: 24px;
        }

        @media screen and (min-width: 992px) {
            display:     flex;
            flex-flow:   column nowrap;
            align-items: stretch;
        }

        &.dark {
            color: #fff;

            background:
                linear-gradient(131deg, #281987 1.41%, #8B439C 76.66%)
            ;
            backdrop-filter: blur(2px);
        }
    }

    .timer-placeholder,
    .timer-element {
        padding: 12px;

        display:         flex;
        flex-flow:       row wrap;
        align-items:     center;
        justify-content: center;

        .timer {
            font-size:   24px;
            line-height: 1.166667;

            width: 139px;
        }
    }

    .timer-placeholder {
        display: none;

        @media screen and (min-width: 992px) {
            display:    flex;
            visibility: hidden;
        }
    }

    .timer-element {
        color: #0E1014;

        background: #FFCE0B;

        @media screen and (min-width: 576px) {
            border-top-right-radius: 24px;
            border-top-left-radius:  24px;
        }
    }

    .checkmark {
        background: var(--color-cases-rivulet); /* #c8fff9; */

        @media screen and (min-width: 576px) {
            border-top-right-radius: 24px;
            border-top-left-radius:  24px;
        }
    }

    :deep(.wc-title) {
        @media screen and (min-width: 992px) {
            font-size:   28px;
            line-height: 1.285714;
        }
    }

    :deep(.wc-price) {
        color:          #fff;
        font-weight:    700;
        text-transform: lowercase;

        background-color: #ED4A79;
        background-image:
            linear-gradient(109deg, #ED4A79 42.02%, #F4912C 84.22%)
        ;
        -webkit-background-clip: text;
                background-clip: text;
        -webkit-text-fill-color: transparent;

        display:         flex;
        flex-flow:       row nowrap;
        align-items:     baseline;
        justify-content: center;

        .wc-price-label,
        .wc-price-original {
            margin-right: 0.25em;
        }

        .wc-price-discount,
        .wc-price-currency {
            margin-left: 0.25em;
        }

        .wc-price-original {
            /* Chrome doesn't support background-clip for strikethrough,
               so fallback */
            color: #ED4A79;

            background: transparent;
            -webkit-background-clip: none;
                    background-clip: none;
            -webkit-text-fill-color: initial;
        }

        .wc-price-current {
            text-transform: none;
        }

        .wc-price-discount {
            color:       var(--color-cases-black);
            font-size:   12px;
            font-weight: 700;
            line-height: 1;

            background: var(--color-cases-cadmium-yellow);
            -webkit-background-clip: none;
                    background-clip: none;
            -webkit-text-fill-color: initial;

            border-radius: 4px;
            padding:       2px;

            align-self: flex-start;

            top: 0;
        }

        .wc-price-currency {
            text-transform: none;
        }

        &.lg {
            font-size:   32px;
            font-weight: normal;
            line-height: 1.125;

            @media screen and (min-width: 576px) {
                font-size:   40px;
                line-height: 1;
            }

            .wc-price-current {
                font-weight: 700;
            }

            .wc-price-currency {
                margin-left: 0;
            }

            .wc-price-discount {
                margin-top: 2px;
            }
        }

        .well-card.dark & {
            color: #FFBE92;

            background: transparent;
            -webkit-background-clip: none;
                    background-clip: none;
            -webkit-text-fill-color: initial;

            .wc-price-original {
                color: inherit;
            }

            .wc-price-discount {
                background: #fff;
            }
        }
    }

    .well-card.dark :deep(.b-link) {
        color: #91D0FF;

        &:hover {
            color: currentColor;
        }
    }

    :deep(.wc-options) {
        margin-right: auto;
        margin-left:  auto;

        flex: 1 0 auto;
    }

    :deep(.wc-option) {
        text-align: left;

        padding: 4px 0;

        display:     flex;
        align-items: flex-start;

        .icon {
            flex:            0 0 auto;
            display:         flex;
            align-items:     center;
            justify-content: center;

            width: 24px;
            height: 24px;
        }

        .icon-ic-tick-thin {font-size: 14px;}
        .icon-ic-favourite-outline {font-size: 18px;}
        .icon-ic-dismiss {font-size: 14px;}
    }

    :deep(.btn-outline) {
        color:          #fff;
        font-size:      14px;
        font-weight:    600;
        line-height:    16px;
        text-align:     center;
        text-transform: uppercase;

        background: transparent;

        box-shadow:
                  0 0 0 0 rgba(27, 139, 224, 0.32),
            inset 0 0 0 2px #fff;
        ;
        cursor: pointer;
        transition:
            color .1s ease,
            box-shadow .1s ease
        ;
        user-select: none;

        outline:       0;
        border:        0;
        border-radius: 999px;
        padding:       8px 16px;

        min-height:      32px;
        display:         flex;
        align-items:     center;
        justify-content: center;

        &:hover {
            color: rgba(255, 255, 255, .8);

            box-shadow:
                      0 0 0 0 rgba(27, 139, 224, 0.32),
                inset 0 0 0 2px rgba(255, 255, 255, .8);
            ;
        }
    }

    :deep(.btn),
    :deep(.btn-secondary) {
        margin-right: auto;
        margin-left:  auto;

        display: flex;
        width:   100%;

        @media screen and (min-width: 576px) {
            padding: 16px;
        }

        @media screen and (min-width: 992px) {
            /* display:   inline-flex;
            width:     auto; */
        }

        & + .btn,
        & + .btn-secondary {
            margin-top: 16px;
        }
    }

    :deep(.wc-muted) {
        margin-right: auto;
        margin-left:  auto;

        /* max-width: 340px; */
    }
</style>
